import { Fragment } from 'react';
import Pluralize from 'react-pluralize';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import duration from 'dayjs/plugin/duration';
import { Tooltip, makeStyles } from '@material-ui/core';
import { FaCheck, FaTimes } from 'react-icons/fa';
import InfoBlock from '../../InfoBlock';
import formatFlightSeats from '../../../helpers/formatFlightSeats';
import { BookingStatus } from '../../../constants';
import Label from '../../Label';
import FlightFareBreakdown from './FlightFareBreakdown';
import TicketNumbers from '../TicketNumbers';

const useStyles = makeStyles(() => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
  },
}));

dayjs.extend(dayjsPluginUTC);
dayjs.extend(duration);

const FlightInfo = ({ itinerary, flightBookingResponse, passengers }) => {
  const classes = useStyles();
  return (
    <>
      {(itinerary.flightSegments).map((item, index) => {
        const durationInMinutes = dayjs.duration(item.durationInMin, 'minutes');
        const flightHours = Math.floor(durationInMinutes.asHours());
        const flightMinutes = durationInMinutes.asMinutes() % 60;
        return (
          <Fragment key={item.departureDateTime + item.carrier + index.toString()}>
            <InfoBlock title={item.carrier}>
              <div className="text-sm">
                Departs on
                {' '}
                {dayjs(item.departureDateTime).format('dddd, MMM DD')}
              </div>
              <div className="text-sm">
                {item.originAirportCode}
                {' '}
                {dayjs(item.departureDateTime).format('h:mma')}
                {' '}
                -
                {' '}
                {item.destinationAirportCode}
                {' '}
                {dayjs(item.arrivalDateTime).format('h:mma')}
              </div>
              <div className="text-sm">
                {flightHours && <Pluralize singular="hour" count={flightHours} />}
                {(flightMinutes) ? (
                  <>
                    {' '}
                    <Pluralize
                      singular="minute"
                      count={flightMinutes}
                    />
                  </>
                ) : ''}
                ,
                {' '}
                <Pluralize singular="stop" count={item.stops} zero="nonstop" />
              </div>

              <div className="text-sm">
                {item.flights.map((flight, flightIndex) => (
                  <Fragment key={flight.flightNumber + flight.carrierCode + flightIndex.toString()}>
                    {flightIndex > 0 ? ', ' : ''}
                    <Tooltip arrow title={flight.carrierFullName} placement="top" classes={classes}>
                      <span className="border-b border-dotted border-current">{flight.carrierCode}</span>
                    </Tooltip>
                    {' '}
                    {flight.flightNumber}
                  </Fragment>
                ))}
              </div>

              <div className="text-sm">
                Brand:
                {' '}
                {item.cabinFareBrandNames}
              </div>
              <div className="text-sm">
                Cabin:
                {' '}
                {item.cabinClassNames}
              </div>
              <div className="text-sm">
                Cancellation:
                {'  '}
                <Label
                  text={item.refundabilityLabel}
                  icon={!item.isRefundable ? <FaTimes /> : <FaCheck />}
                  level={!item.isRefundable ? 'danger' : 'success'}
                  descriptionOnHover={item.partialRefundPolicyText}
                  tooltipPlacement="top"
                  italic
                  inline
                  capitalize
                />
              </div>
              {flightBookingResponse && flightBookingResponse.flightSegments
              && flightBookingResponse.flightSegments[index] && (
                <div className="text-sm text-gray-600">
                  Status:
                  {' '}
                  {flightBookingResponse.flightSegments[index].bookingStatus
                    || BookingStatus.failed}
                </div>
              )}
              {passengers && formatFlightSeats(item.flights, passengers) && (
              <div className="text-sm text-gray-600">
                Seats:
                {' '}
                {formatFlightSeats(item.flights, passengers)}
              </div>
              )}
            </InfoBlock>
          </Fragment>
        );
      })}
      <FlightFareBreakdown flightItinerary={itinerary} />
      <TicketNumbers passengers={passengers} doShowFlightTickets />
    </>
  );
};

FlightInfo.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  flightBookingResponse: PropTypes.shape(),
  passengers: PropTypes.arrayOf(PropTypes.shape()),
};

FlightInfo.defaultProps = {
  flightBookingResponse: null,
  passengers: null,
};

export default FlightInfo;
